import { Col } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';
import {
  MessageBanner,
  MessageBannerType,
} from '@amzn/stencil-react-components/message-banner';

import { useMessages } from '../../../shared/i18n/useMessages';
import {
  SubQuestionViolationKind,
  type SubQuestionViolation,
} from '../../../shared/domain/subQuestionValidation';

const BUNDLE = 'features.audit-whiz.translations.reviewQuestions';

type BannerKey =
  | 'subUntitledLabel'
  | 'publishBlockedTitle'
  | 'publishBlockedMinOptions'
  | 'publishBlockedEmptyTitle'
  | 'publishBlockedEmptyInstruction'
  | 'publishBlockedTaskIncomplete';

/** The banner line i18n key for each violation kind — so a blank title shows
 *  "needs a title", not the options message. A switch (not a keyed object)
 *  stays exhaustive over the union and dodges the dynamic-object-index lint. */
function messageKey(kind: SubQuestionViolationKind): BannerKey {
  switch (kind) {
    case SubQuestionViolationKind.EMPTY_TITLE:
      return 'publishBlockedEmptyTitle';
    case SubQuestionViolationKind.EMPTY_INSTRUCTION:
      return 'publishBlockedEmptyInstruction';
    case SubQuestionViolationKind.MIN_OPTIONS:
      return 'publishBlockedMinOptions';
    case SubQuestionViolationKind.TASK_INCOMPLETE:
      return 'publishBlockedTaskIncomplete';
  }
}

export interface PublishBlockedBannerProps {
  /** The live publish-block violations, or null when the draft is publishable.
   *  Renders nothing when null/empty. */
  readonly violations: readonly SubQuestionViolation[] | null;
  readonly dataTestId?: string;
}

/**
 * The top-of-wizard error banner naming each sub-question that blocks Save &
 * publish, one line per violation with a kind-specific message. Non-dismissible
 * — it's derived live from the wizard's publishBlock, so it clears itself the
 * instant the issue is fixed (a dismiss would just reappear on the next edit).
 */
export function PublishBlockedBanner({
  violations,
  dataTestId,
}: PublishBlockedBannerProps): JSX.Element | null {
  const { t, f } = useMessages<BannerKey>(BUNDLE);
  if (violations === null || violations.length === 0) return null;
  return (
    <MessageBanner
      type={MessageBannerType.Error}
      isDismissible={false}
      titleText={t('publishBlockedTitle')}
      {...(dataTestId !== undefined ? { dataTestId } : {})}
    >
      <Col gridGap="dimensions.spacing.50">
        {violations.map((violation) => (
          <Text key={`${violation.index}-${violation.kind}`} fontSize="T100">
            {f(messageKey(violation.kind), {
              number: violation.index,
              // A blank title shows the same "Untitled sub question" fallback
              // the row header uses — never an empty ("").
              title: violation.title.trim() || t('subUntitledLabel'),
            })}
          </Text>
        ))}
      </Col>
    </MessageBanner>
  );
}
